import React, { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import {
  BoundDatasetsObjectMapping,
  Maybe,
  MdmObject,
  MdmObjectDto,
  ObjectType,
  useDeleteObjectMutation,
  useGetCoordinatesLazyQuery,
  useGetObjectsByProjectQuery,
  useGetProjectQuery,
} from "gql/generated/types";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Chips, ChipsChangeEvent } from "primereact/chips";
import { confirmPopup } from "primereact/confirmpopup";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabPanelHeaderTemplateOptions, TabView } from "primereact/tabview";
import { Controller, ControllerRenderProps, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { z } from "zod";
import { apolloErrorToaster, getFormErrorMessage, successToaster } from "../utils/utils";
import AdditionalInfo from "./AdditionalInfo";
import AdditionalInfoEdit from "./AdditionalInfoEdit";
import DatasetsTab from "./DatasetsTab";
import ObjectAnalytics from "./ObjectAnalytics";
import ObjectDevices from "./ObjectDevices";
import ObjectJobs from "./ObjectJobs";
import ObjectNodes from "./ObjectNodes";
import ObjectNotes from "./ObjectNotes";

type ObjectFormProps = {
  object: MdmObjectDto | null;
  onSubmit: (object: MdmObjectDto) => void;
  loading?: boolean;
  objectType: ObjectType;
};

const emptyObject: Partial<MdmObjectDto> = {
  tags: [],
};

const schema = z.object({
  title: z.string().min(1, T("word.required-fields")),
});

const ObjectForm: React.FC<ObjectFormProps> = ({ object, onSubmit, loading, objectType }) => {
  const [objectState, setObjectState] = useState<MdmObjectDto>(object ?? (emptyObject as MdmObjectDto));
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const { projectId } = useParams();

  const { data: objectData } = useGetObjectsByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });

  const { data: projectData } = useGetProjectQuery({
    variables: { id: projectId! },
    skip: !projectId,
  });

  const { data: objects, loading: objectsLoading } = useGetObjectsByProjectQuery({
    variables: { id: projectId! },
    skip: !projectId && object?.type === ObjectType.Child,
    onError: apolloErrorToaster,
  });

  const info = (() => {
    switch (object?.type) {
      case ObjectType.Object:
        return {
          attributes: projectData?.mdmProjectById?.configuration?.objectConfiguration.customFields ?? [],
          captionName: T("mdm.object-details"),
          codeName: T("mdm.object-code"),
        };
      case ObjectType.Child:
        return {
          attributes:
            projectData?.mdmProjectById?.configuration?.childrenConfiguration.find(
              c => c?.childType === object.type
            )?.customFields ?? [],
          captionName: T("mdm.apartment-details"),
          codeName: T("mdm.object-code"),
        };
      default:
        return {
          attributes: [],
          captionName: "details",
          codeName: "code",
        };
    }
  })();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const [deleteObjectMutation, { loading: loadingDelete }] = useDeleteObjectMutation({
    onCompleted: () => {
      successToaster(T("alert.object-deleted-successfully"));
      navigate(`/mdm/${projectId}/objects`);
    },
    onError: apolloErrorToaster,
  });

  const [getCoordinates, { data, loading: loadingCoord, error }] = useGetCoordinatesLazyQuery();

  useEffect(() => {
    if (data && data.coordinates) {
      setObjectState(prev => ({
        ...prev,
        latitude: data.coordinates[0]?.latitude,
        longitude: data.coordinates[0]?.longitude,
      }));
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | ChipsChangeEvent) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, address: { ...prev.address, [name]: value } }) as MdmObjectDto);
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (object) {
          deleteObjectMutation({
            variables: { id: object.id! },
            update(cache) {
              const normalizedId = cache.identify({ id: object.id, __typename: "MdmObjectDTO" });
              cache.evict({ id: normalizedId });
              cache.gc();
            },
          });
        }
      },
    });
  };

  const renderTitleControl = (field: ControllerRenderProps<Partial<MdmObject>, any>) => (
    <InputLabelWrapper labelName={T("word.object-name")} col={4}>
      <InputText
        value={field.value}
        onChange={e => {
          field.onChange(e.target.value);
          handleChange(e);
        }}
        className="p-inputtext-sm"
        name="title"
        placeholder={T("word.object-name")}
      />
      {getFormErrorMessage("title", errors)}
    </InputLabelWrapper>
  );

  const tabHeaderTemplate = (options: TabPanelHeaderTemplateOptions, count: number) => (
    <div
      className={`p-tabview-nav-link${count === 0 ? "" : " p-3"}`}
      onClick={options.onClick}
      role="tab"
      tabIndex={0}
    >
      <span className="p-tabview-title">{options.titleElement}</span>
      {count !== 0 && <Badge value={count} className="ml-2" />}
    </div>
  );

  const parentObjectRow = (
    <tr>
      <th>{T("mdm.parent-object")}:</th>
      <td>
        {!object?.parentId && "-"}
        {object?.parentId && objectsLoading && <i className="pi pi-spin pi-spinner" />}
        {object?.parentId && !objectsLoading && (
          <Link to={`../objects/${object?.parentId}`}>
            {objects?.mdmObjectsByProjectId.find(o => o.id === object?.parentId)?.identifier ?? "-"}
          </Link>
        )}
      </td>
    </tr>
  );

  return (
    <div className="flex justify-content-between flex-column gap-5 mx-auto mt-5">
      {!editMode && object && (
        <div>
          <div className="text-right">
            {object?.latitude && object?.longitude && (
              <Button
                type="button"
                icon="pi pi-map"
                title={T("word.show-on-map")}
                className="p-button-rounded p-button-text"
                onClick={() => navigate(`../map/${object?.id}`)}
              />
            )}
            <Button
              onClick={() => setEditMode(true)}
              type="button"
              title={T("action.edit")}
              className="p-button-rounded p-button-text p-button-sm"
              icon="pi pi-pencil"
            />
          </div>

          <div className="info-table-wrapper">
            <table className="info-table">
              <caption>{info.captionName}</caption>
              <tbody>
                <tr>
                  <th>{info.codeName}:</th>
                  <td>{object?.identifier ?? "-"}</td>
                </tr>
                {object.type === ObjectType.Child && parentObjectRow}
                <tr>
                  <th>{T("word.title")}:</th>
                  <td>{object?.title ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.description")}:</th>
                  <td>{object?.description ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.address")}:</th>
                  <td>{object?.address?.fullAddress ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.latitude")}:</th>
                  <td>{object?.latitude?.toFixed(5) ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.longitude")}:</th>
                  <td>{object?.longitude?.toFixed(5) ?? "-"}</td>
                </tr>
                <tr>
                  <th>{T("word.tags")}:</th>
                  <td>{object?.tags?.length ? object?.tags?.join(", ") : "-"}</td>
                </tr>
              </tbody>
            </table>
            {object && info.attributes.length > 0 && (
              <AdditionalInfo attributes={info.attributes} objectState={objectState} />
            )}
          </div>
        </div>
      )}
      {(editMode || !object) && (
        <form>
          <div className="grid p-fluid">
            <InputLabelWrapper labelName={T("mdm.object-code")} col={2}>
              <InputText
                value={objectState.identifier ?? ""}
                onChange={handleChange}
                className="p-inputtext-sm"
                name="identifier"
                placeholder={T("mdm.object-code")}
              />
            </InputLabelWrapper>
            <Controller
              defaultValue={object?.title ?? ""}
              control={control}
              name="title"
              render={({ field }) => renderTitleControl(field)}
            />
            <InputLabelWrapper labelName={T("word.description")}>
              <InputText
                value={objectState.description ?? ""}
                onChange={handleChange}
                className="p-inputtext-sm"
                name="description"
                placeholder={T("word.description")}
              />
            </InputLabelWrapper>
            {objectType === ObjectType.Object && (
              <>
                <InputLabelWrapper labelName={T("word.country")} col={1}>
                  <InputText
                    value={objectState.address?.country ?? ""}
                    onChange={handleAddressChange}
                    className="p-inputtext-sm"
                    name="country"
                    placeholder={T("word.country")}
                  />
                </InputLabelWrapper>
                <InputLabelWrapper labelName={T("word.city")} col={1}>
                  <InputText
                    value={objectState.address?.city ?? ""}
                    onChange={handleAddressChange}
                    className="p-inputtext-sm"
                    name="city"
                    placeholder={T("word.city")}
                  />
                </InputLabelWrapper>
                <InputLabelWrapper labelName={T("word.street")} col={2}>
                  <InputText
                    value={objectState.address?.street ?? ""}
                    onChange={handleAddressChange}
                    className="p-inputtext-sm"
                    name="street"
                    placeholder={T("word.street")}
                  />
                </InputLabelWrapper>
                <InputLabelWrapper labelName={T("word.house")} col={1}>
                  <InputText
                    value={objectState.address?.houseNumber ?? ""}
                    onChange={handleAddressChange}
                    className="p-inputtext-sm"
                    name="houseNumber"
                    placeholder={T("word.house-number")}
                  />
                </InputLabelWrapper>
                <InputLabelWrapper labelName={T("word.post-code")} col={1}>
                  <InputText
                    value={objectState.address?.postCode ?? ""}
                    onChange={handleAddressChange}
                    className="p-inputtext-sm"
                    name="postCode"
                    placeholder={T("word.post-code")}
                  />
                </InputLabelWrapper>
                <div className="col-2" />
                <InputLabelWrapper labelName={`${T("word.latitude")}/${T("word.longitude")}`} col={4}>
                  <div className="p-inputgroup">
                    <Button
                      type="button"
                      loading={loadingCoord}
                      label={T("action.get-coordinates")}
                      onClick={() => {
                        const { fullAddress, ...address } = objectState.address!;
                        getCoordinates({
                          variables: { address },
                        });
                      }}
                    />
                    <InputNumber
                      value={objectState.latitude}
                      onChange={e =>
                        setObjectState(prev => ({
                          ...prev,
                          latitude: e.value,
                        }))
                      }
                      className="p-inputtext-sm w-4"
                      name="latitude"
                      placeholder={T("word.latitude")}
                      useGrouping={false}
                      maxFractionDigits={10}
                    />
                    <InputNumber
                      value={objectState.longitude}
                      onChange={e =>
                        setObjectState(prev => ({
                          ...prev,
                          longitude: e.value,
                        }))
                      }
                      className="p-inputtext-sm w-4"
                      name="longitude"
                      placeholder={T("word.longitude")}
                      useGrouping={false}
                      maxFractionDigits={10}
                    />
                  </div>
                </InputLabelWrapper>
              </>
            )}
            {objectType === ObjectType.Child && (
              <InputLabelWrapper labelName={T("word.object")} col={12}>
                <Dropdown
                  value={objectState.parentId}
                  options={objectData?.mdmObjectsByProjectId.map(o => ({
                    identifier: o.identifier,
                    id: o.id,
                  }))}
                  onChange={e => setObjectState(prev => ({ ...prev, parentId: e.value }))}
                  optionLabel="identifier"
                  optionValue="id"
                  name="mdmObject"
                  placeholder={T("word.object")}
                  className="p-inputtext-sm w-full md:w-4"
                  filter
                />
              </InputLabelWrapper>
            )}
            {error && <div className="flex align-items-end col-6 text-red-500">{error.message}</div>}
            <InputLabelWrapper labelName={T("word.tags")}>
              <Chips
                value={(objectState.tags as string[]) ?? []}
                onChange={handleChange}
                name="tags"
                placeholder={`${T("word.tags")}..`}
              />
            </InputLabelWrapper>
            {object && info.attributes.length > 0 && (
              <AdditionalInfoEdit
                attributes={info.attributes}
                objectState={objectState}
                setObjectState={setObjectState}
                editMode={editMode}
              />
            )}
          </div>
          <div className="flex w-full justify-content-end gap-3 mt-3">
            <Button
              type="submit"
              onClick={handleSubmit(() => {
                onSubmit({ ...objectState, type: objectType });
                setEditMode(false);
              })}
              label={object ? T("action.update") : T("action.create")}
              loading={loading}
              className={`p-button-rounded p-button-${object ? "primary" : "success"} w-10rem`}
            />
            {object && (
              <>
                <Button
                  type="button"
                  label={T("action.delete")}
                  className="p-button-rounded p-button-danger w-10rem"
                  onClick={handleDelete}
                  loading={loadingDelete}
                />
                <Button
                  type="button"
                  onClick={() => {
                    setObjectState(object ?? emptyObject);
                    setEditMode(false);
                  }}
                  label={T("word.cancel")}
                  className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
                />
              </>
            )}
            {!object && (
              <Button
                type="button"
                onClick={() => navigate(-1)}
                label={T("action.back")}
                className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
              />
            )}
          </div>
        </form>
      )}

      {!editMode && object && (
        <TabView className="col-12">
          <TabPanel header={T("mdm.devices")}>
            <ObjectDevices object={object} />
          </TabPanel>
          {object.children && (
            <TabPanel header={T("mdm.apartments")}>
              <ObjectNodes object={object} />
            </TabPanel>
          )}
          <TabPanel header={T("word.datasets")}>
            <ObjectDatasets object={object} />
          </TabPanel>
          <TabPanel header={T("mdm.analytics")}>
            <ObjectAnalytics object={object} tableName="AnalyticsByObject" />
          </TabPanel>
          <TabPanel
            header={T("word.notes")}
            headerTemplate={options => tabHeaderTemplate(options, object.info.notesCount)}
          >
            <ObjectNotes object={object} />
          </TabPanel>
          <TabPanel
            header={T("mdm.jobs")}
            headerTemplate={options => tabHeaderTemplate(options, object.info.jobsCount)}
          >
            <ObjectJobs object={object} tableName="JobsByObject" />
          </TabPanel>
        </TabView>
      )}
    </div>
  );
};

export default ObjectForm;

const ObjectDatasets: React.FC<{ object: MdmObjectDto }> = ({ object }) => {
  const options = [
    { title: T("mdm.all-datasets"), data: object.boundDatasets.all },
    { title: T("mdm.object-datasets"), data: object.boundDatasets.direct },
    { title: T("mdm.devices-datasets"), data: object.boundDatasets.indirect },
  ];

  const [datasets, setDatasets] = useState<{
    title: string;
    data: Maybe<BoundDatasetsObjectMapping>[];
  }>(options[1]);

  return (
    <div className="flex flex-column align-items-start">
      <Dropdown
        value={datasets}
        options={options}
        optionLabel="title"
        onChange={e => setDatasets(e.value)}
        className="ml-3"
      />
      <DatasetsTab datasets={datasets.data?.map(d => d?.dataset!)} />
    </div>
  );
};
